/**
 * SVG sanitization utility.
 *
 * SVG markup that is fetched/imported at runtime and inlined into the DOM via
 * `dangerouslySetInnerHTML` is an XSS vector if the source is ever
 * attacker-controlled. This module hardens that path with DOMPurify configured
 * for an SVG profile, stripping `<script>`, `foreignObject` scripts, `on*`
 * event-handler attributes, `javascript:` URLs, and similar dangerous content.
 *
 * In the browser DOMPurify uses the global `window` with zero configuration.
 * Under a jsdom test environment (`// @vitest-environment jsdom`) the same
 * global-window path works, so no special wiring is required.
 */
import DOMPurify from 'dompurify'

/**
 * Sanitize an SVG string so it is safe to inject via `dangerouslySetInnerHTML`.
 *
 * Removes scripts, event handlers, and `javascript:`/`data:` script vectors
 * while keeping benign SVG shape/structure elements (paths, groups, etc.).
 *
 * @param svg Raw SVG markup.
 * @returns Sanitized SVG markup. Returns an empty string for empty/invalid input.
 */
export function sanitizeSvg(svg: string): string {
  if (!svg) return ''

  return DOMPurify.sanitize(svg, {
    // Enable the SVG (and SVG filter) profiles so legitimate SVG markup
    // survives sanitization.
    USE_PROFILES: { svg: true, svgFilters: true },
    // Belt-and-suspenders: explicitly forbid script-bearing tags. DOMPurify's
    // SVG profile already drops these, but forbidding them is defense in depth.
    FORBID_TAGS: ['script', 'foreignObject'],
    // DOMPurify strips all `on*` event-handler attributes by default; we keep
    // it explicit here for clarity. Returning a DOM string (not a node).
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  })
}

export default sanitizeSvg
